import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthentication } from './Authentication/useAuthentication';
import {
  asideBarList,
  asideBarListSetting,
} from './Shared/Layout/Aside/AsideBarList';
import { checkIfFoodAdmin, checkIfUserSuperAdmin } from '~/utils/getUserRole';
import { headerBarList } from '~/constants/headerBarList';

interface PrivateRouteProps {
  children: ReactNode;
}

function PrivateRoute({ children }: PrivateRouteProps) {
  const location = useLocation();
  const { authenticated, user } = useAuthentication();
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const isFoodAdmin = checkIfFoodAdmin(user?.data.role_name);
  const allowedRoutes = user?.data.sections;
  const allowedAssetRoutes = user?.data.admin_sections;
  const headerLinks = headerBarList();

  const newAllowedAssetRoutes = allowedAssetRoutes?.map(item => {
    if (item.type === 'knowledge_base') {
      const newType = 'knowledge-base';
      return {
        ...item,
        type: newType,
      };
    } else if (item.type === 'storage') {
      return {
        ...item,
        type: 'documents',
      };
    } else if (item.type === 'games') {
      const newType = 'entertainment';
      return {
        ...item,
        id: 10,
        type: newType,
      };
    } else {
      return item;
    }
  });

  const newAllowedRoutes = allowedRoutes?.map(item => {
    if (item.type === 'knowledge_base') {
      const newType = 'knowledge-base';
      return {
        ...item,
        type: newType,
      };
    } else if (item.type === 'storage') {
      return {
        ...item,
        type: 'documents',
      };
    } else {
      return item;
    }
  });

  const defaultPath = asideBarList.find(item => {
    if (!!allowedRoutes?.length) {
      return item.sectionId === allowedRoutes[0].pivot.section_id;
    }
    return { path: '/settings' };
  });

  const currentPath = asideBarList.find(item => {
    return location.pathname.includes(item.path);
  });

  const currentPathHeader = headerLinks?.find(item =>
    location.pathname.toLowerCase().includes(item.path.toLowerCase())
  );

  const currentPathSettings = asideBarListSetting.find(item =>
    location.pathname.includes(item.path)
  );

  const isUserCardRoute = /^\/user\/\d+/.test(location.pathname);

  const isAccess =
    location.pathname === '/settings' ||
    (isUserCardRoute && !!allowedAssetRoutes?.length) ||
    newAllowedRoutes?.some(item => {
      return (
        currentPath?.sectionId === item.pivot.section_id ||
        currentPathSettings?.sectionId === item.pivot.section_id ||
        currentPathHeader?.path.includes(item.type.toLowerCase())
      );
    });

  const isAssetAccess =
    location.pathname === '/settings' ||
    location.pathname === '/dashboard' ||
    (isUserCardRoute && !!allowedAssetRoutes?.length) ||
    newAllowedAssetRoutes?.some(item => {
      return (
        currentPath?.sectionId === item.id ||
        currentPathSettings?.sectionId === item.id ||
        currentPathHeader?.path.includes(item.type.toLowerCase())
      );
    });

  if (!authenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  } else if (
    authenticated &&
    (!isAccess || !isAssetAccess) &&
    !isSuperAdmin &&
    !isFoodAdmin
  ) {
    return (
      <Navigate
        to={defaultPath?.path || '/settings'}
        state={{ from: location }}
      />
    );
  } else {
    return children;
  }
}

export default PrivateRoute;
